import { FC, useState } from "react";
import { Checkbox, Switch } from "antd";
import {
    Button,
    Space,
    SimpleCard,
    Typography,
} from "../../../design-system";
import {
    Project,
    ProjectAction,
    RfxVendor,
    RfxVendorStatus,
    canDo,
} from "../../../types/ProjectTypes";
import { CompanyImage } from "../../../common/components/CompanyImage";
import {
    selectRfxVendors,
    setRfxVendorsSelected,
    unselectRfxVendors,
} from "../../../common/api/RfxClient";
import { handleError } from "../../../common/ErrorHandling";
import { ProjectSummaryCard } from "./ProjectSummaryCard";
import { SpawnDealModal } from "./SpawnDealModal";
import styles from "./CompletedRfxView.module.less";

interface CompletedRfxViewProps {
    project: Project;
    onUpdate: () => void;
}

/**
 * The project-page body for a COMPLETED RFx project: one card per evaluated
 * vendor candidate. Users toggle selection and spawn deal-analysis projects
 * from here.
 */
export const CompletedRfxView: FC<CompletedRfxViewProps> = ({
    project,
    onUpdate,
}) => {
    const rfx = project.rfx;
    const vendors = rfx?.vendors ?? [];
    const canEdit = canDo(project, ProjectAction.EDIT);
    const hasSelected = vendors.some((v) => v.status === RfxVendorStatus.SELECTED);
    const showList = rfx?.vendorsSelected ?? false;
    const [toggling, setToggling] = useState(false);

    if (!rfx || vendors.length === 0) {
        return null;
    }

    const onToggleChange = async (checked: boolean) => {
        if (toggling) return;
        setToggling(true);
        try {
            await setRfxVendorsSelected(rfx.id, checked);
            onUpdate();
        } catch (err) {
            handleError(err);
        } finally {
            setToggling(false);
        }
    };

    const header = (
        <div className={styles.header}>
            <span>Did you select one or more vendors?</span>
            <Switch
                checked={showList}
                loading={toggling}
                disabled={!canEdit || (showList && hasSelected)}
                onChange={onToggleChange}
            />
        </div>
    );

    return (
        <SimpleCard header={header} headerType="primary">
            <div className={showList ? undefined : styles.disabled}>
                <Space direction="vertical" size="middle" fullWidth>
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Select the vendor(s) you want to negotiate with. You can create a
                        deal analysis project per vendor once selected.
                    </Typography.Paragraph>
                    <div className={styles.grid}>
                        {vendors.map((v) => (
                            <VendorCard
                                key={v.vendorKey}
                                rfxProject={project}
                                vendor={v}
                                canEdit={canEdit && showList}
                                onChanged={onUpdate}
                            />
                        ))}
                    </div>
                </Space>
            </div>
        </SimpleCard>
    );
};

interface VendorCardProps {
    rfxProject: Project;
    vendor: RfxVendor;
    canEdit: boolean;
    onChanged: () => void;
}

const VendorCard: FC<VendorCardProps> = ({ rfxProject, vendor, canEdit, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const [localStatus, setLocalStatus] = useState<RfxVendorStatus>(vendor.status);
    const [spawnModalOpen, setSpawnModalOpen] = useState(false);
    const hasDeal = !!vendor.dealProject;
    const isSelected = localStatus === RfxVendorStatus.SELECTED;

    // Once a deal has been spawned, render the vendor row as the deal's
    // project card — clicking it navigates to the deal.
    if (hasDeal && vendor.dealProject) {
        return <ProjectSummaryCard project={vendor.dealProject} onUpdate={onChanged} />;
    }

    const onToggle = async (checked: boolean) => {
        if (busy || !canEdit) return;
        const prev = localStatus;
        setLocalStatus(checked ? RfxVendorStatus.SELECTED : RfxVendorStatus.CANDIDATE);
        setBusy(true);
        try {
            const rfxId = rfxProject.rfx!.id;
            if (checked) {
                await selectRfxVendors(rfxId, [vendor.vendorKey]);
            } else {
                await unselectRfxVendors(rfxId, [vendor.vendorKey]);
            }
            onChanged();
        } catch (err) {
            setLocalStatus(prev);
            handleError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <SimpleCard type="white">
                <div className={styles.card}>
                    <div className={styles.checkbox}>
                        <Checkbox
                            checked={isSelected}
                            disabled={!canEdit || busy}
                            onChange={(e) => onToggle(e.target.checked)}
                        />
                    </div>
                    <div className={styles.logo}>
                        <CompanyImage logo={vendor.vendorHero} size="small" name={vendor.vendorName} />
                    </div>
                    <div className={styles.body}>
                        <div className={styles.name}>{vendor.vendorName}</div>
                    </div>
                    {isSelected && canEdit && (
                        <Button type="primary" onClick={() => setSpawnModalOpen(true)}>
                            Create deal analysis
                        </Button>
                    )}
                </div>
            </SimpleCard>
            {spawnModalOpen && (
                <SpawnDealModal
                    open={spawnModalOpen}
                    rfxProject={rfxProject}
                    vendor={vendor}
                    onClose={() => setSpawnModalOpen(false)}
                    onSuccess={onChanged}
                />
            )}
        </>
    );
};
